import type { PaletteOptions } from "@mui/material/styles";
import type { DesignTokens } from "./types";
import { chartColors } from "./tokens";

// ─── MUI Palette Augmentation ────────────────────────────────────────
// Allows consumers to access chart tokens via theme.palette.chart

// Allow Chip to accept color="highlight" (rendered via variants in components.ts)
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    highlight: true;
  }
}

// Allow Alert to accept variant="featured" (rendered via variants in components.ts)
declare module "@mui/material/Alert" {
  interface AlertPropsVariantOverrides {
    featured: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    chart: DesignTokens["chartColors"];
    highlight: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    chart?: DesignTokens["chartColors"];
    highlight?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
  interface TypeText {
    placeholder: string;
  }
}

export function buildPalette(tokens: DesignTokens): PaletteOptions {
  const { base, error, warning, success, info, text, bg, fg, border } = tokens;

  return {
    primary: {
      main: tokens.bg.brandSolid,
      light: tokens.fg.brandPrimary,
      dark: tokens.bg.brandSolidHover,
      contrastText: tokens.base.white,
    },
    secondary: {
      main: tokens.bg.secondarySolid,
      light: tokens.fg.secondary,
      dark: tokens.text.secondaryHover,
      contrastText: tokens.base.white,
    },
    error: {
      main: tokens.error[600],
      light: tokens.error[300],
      dark: tokens.error[700],
      contrastText: tokens.base.white,
    },
    warning: {
      main: tokens.warning[600],
      light: tokens.warning[300],
      dark: tokens.warning[700],
      contrastText: tokens.base.white,
    },
    success: {
      main: tokens.success[600],
      light: tokens.success[300],
      dark: tokens.success[700],
      contrastText: tokens.base.white,
    },
    info: {
      main: tokens.info[600],
      light: tokens.info[300],
      dark: tokens.info[700],
      contrastText: tokens.base.white,
    },
    background: {
      default: bg.primary,
      paper: bg.secondary,
    },
    text: {
      primary: text.primary,
      secondary: text.secondary,
      disabled: text.disabled,
      placeholder: text.placeholder,
    },
    divider: border.secondary,
    action: {
      active: fg.tertiary,
      hover: bg.primaryHover,
      selected: bg.brandPrimary,
      disabled: fg.disabled,
      disabledBackground: bg.disabled,
    },
    chart: tokens.chartColors ?? chartColors,
    ...(tokens.highlight && {
      highlight: {
        main: tokens.highlight[600],
        light: tokens.highlight[300],
        dark: tokens.highlight[700],
        contrastText: tokens.base.white,
      },
    }),
  };
}

